import React, { useEffect, useRef, useState } from "react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAuthorArticles, formatDate } from "@/lib/utils2";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Pencil, Loader2, Globe, Lock, X } from "lucide-react";

function articleImg(a) {
  if (!a.photos || a.photos.length === 0) return null;
  const p = a.photos[0];
  if (p.startsWith("http")) return p;
  return mediaUrl(p);
}

function ArticleForm({ article, onDone }) {
  const isEdit = !!article;
  const [form, setForm] = useState({
    title: article?.title || "",
    body: article?.body || "",
    visibility: article?.visibility || "global",
    stores: (article?.allowed_stores || []).join(", "),
  });
  const [photos, setPhotos] = useState(article?.photos || []);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "article");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.path]);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        photos,
        visibility: form.visibility,
        allowed_stores: form.visibility === "restricted" ? form.stores.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (isEdit) await api.put(`/articles/${article.id}`, payload);
      else await api.post("/articles", payload);
      toast.success(isEdit ? "Article updated" : "Article published");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin pr-1">
      <div><Label>Title</Label><Input data-testid="article-title" value={form.title} onChange={set("title")} className="mt-1.5 border-brand-line" /></div>
      <div><Label>Body</Label><Textarea data-testid="article-body" value={form.body} onChange={set("body")} rows={8} className="mt-1.5 border-brand-line" /></div>
      <div>
        <Label>Photos</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {photos.map((p, i) => (
            <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-brand-line">
              <img src={p.startsWith("http") ? p : mediaUrl(p)} alt="" className="h-full w-full object-cover" />
              <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X className="w-3 h-3 text-white" /></button>
            </div>
          ))}
          <input ref={fileInput} type="file" accept="image/*" hidden data-testid="article-photo-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          <button onClick={() => fileInput.current.click()} className="h-16 w-16 rounded-lg border border-dashed border-brand-line flex items-center justify-center text-brand-muted hover:bg-brand-sidebar">
            <Upload className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div>
        <Label>Visibility</Label>
        <div className="flex gap-2 mt-1.5">
          <button type="button" onClick={() => setForm({ ...form, visibility: "global" })} data-testid="visibility-global"
            className={`flex-1 px-4 py-2 rounded-lg text-sm border flex items-center justify-center gap-2 ${form.visibility === "global" ? "bg-brand text-white border-brand" : "border-brand-line"}`}>
            <Globe className="w-4 h-4" /> All cafes
          </button>
          <button type="button" onClick={() => setForm({ ...form, visibility: "restricted" })} data-testid="visibility-restricted"
            className={`flex-1 px-4 py-2 rounded-lg text-sm border flex items-center justify-center gap-2 ${form.visibility === "restricted" ? "bg-brand text-white border-brand" : "border-brand-line"}`}>
            <Lock className="w-4 h-4" /> Specific stores
          </button>
        </div>
        {form.visibility === "restricted" && (
          <Input data-testid="allowed-stores" value={form.stores} onChange={set("stores")} placeholder="Store numbers, comma-separated e.g. 1024, 2088" className="mt-2 border-brand-line" />
        )}
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={busy} className="rounded-full brand-btn" data-testid="save-article-button">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEdit ? "Save changes" : "Publish article"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function ArticleCard({ article, onOpen, testid }) {
  const img = articleImg(article);
  return (
    <button onClick={onOpen} data-testid={testid} className="text-left lift rounded-2xl border border-brand-line bg-white overflow-hidden">
      {img && <img src={img} alt="" className="h-44 w-full object-cover" />}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          {article.visibility === "global"
            ? <Badge className="rounded-full border-0 bg-brand/10 text-brand"><Globe className="w-3 h-3 mr-1" /> All cafes</Badge>
            : <Badge className="rounded-full border-0 bg-brand-dough/25 text-brand-ink"><Lock className="w-3 h-3 mr-1" /> {(article.allowed_stores || []).length} store(s)</Badge>}
        </div>
        <h3 className="font-display text-2xl text-brand-ink">{article.title}</h3>
        <p className="text-brand-muted mt-2 line-clamp-2 leading-relaxed">{article.body}</p>
        <p className="text-xs text-brand-muted mt-3">{article.author_name} · {formatDate(article.created_at)}</p>
      </div>
    </button>
  );
}

export default function Articles() {
  const { user } = useAuth();
  const canAuthor = canAuthorArticles(user);
  const [articles, setArticles] = useState([]);
  const [manage, setManage] = useState([]);
  const [reading, setReading] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/articles");
    setArticles(data.articles);
  };
  const loadManage = async () => {
    if (!canAuthor) return;
    const { data } = await api.get("/articles/manage");
    setManage(data.articles);
  };
  useEffect(() => { load(); loadManage(); /* eslint-disable-next-line */ }, []);

  const remove = async (a) => {
    try { await api.delete(`/articles/${a.id}`); toast.success("Article removed"); load(); loadManage(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggleHide = async (a) => {
    try { await api.put(`/articles/${a.id}`, { status: a.status === "hidden" ? "published" : "hidden" }); load(); loadManage(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const Grid = (list, tprefix) => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
      {list.length === 0 && <p className="text-brand-muted text-sm col-span-full text-center py-10">No articles yet.</p>}
      {list.map((a) => <ArticleCard key={a.id} article={a} onOpen={() => setReading(a)} testid={`${tprefix}-${a.id}`} />)}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <PageHeader title="Articles" subtitle="Written coaching on brand standards and food safety, tailored to your cafe.">
        {canAuthor && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full brand-btn" data-testid="new-article-button"><Plus className="w-4 h-4 mr-2" /> New article</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl border-brand-line">
              <DialogHeader><DialogTitle className="font-display text-2xl">Write article</DialogTitle></DialogHeader>
              <ArticleForm onDone={() => { setCreateOpen(false); load(); loadManage(); }} />
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {canAuthor ? (
        <Tabs defaultValue="read">
          <TabsList className="bg-brand-sidebar h-auto p-1 rounded-full mb-6">
            <TabsTrigger value="read" className="rounded-full data-[state=active]:bg-white px-5 py-2" data-testid="tab-read">Read</TabsTrigger>
            <TabsTrigger value="manage" className="rounded-full data-[state=active]:bg-white px-5 py-2" data-testid="tab-article-manage">Manage</TabsTrigger>
          </TabsList>
          <TabsContent value="read">{Grid(articles, "article-card")}</TabsContent>
          <TabsContent value="manage">
            <div className="space-y-3">
              {manage.map((a) => (
                <div key={a.id} className="rounded-xl border border-brand-line bg-white p-4 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-brand-ink truncate">{a.title}</p>
                      {a.status === "hidden" && <Badge className="rounded-full border-0 bg-brand-muted/15 text-brand-muted">hidden</Badge>}
                    </div>
                    <p className="text-sm text-brand-muted">{a.visibility === "global" ? "All cafes" : `${a.allowed_stores?.length || 0} store(s)`} · {formatDate(a.created_at)}</p>
                  </div>
                  <Dialog open={editing?.id === a.id} onOpenChange={(o) => setEditing(o ? a : null)}>
                    <DialogTrigger asChild><Button size="icon" variant="ghost" data-testid={`edit-article-${a.id}`}><Pencil className="w-4 h-4" /></Button></DialogTrigger>
                    <DialogContent className="max-w-2xl border-brand-line">
                      <DialogHeader><DialogTitle className="font-display text-2xl">Edit article</DialogTitle></DialogHeader>
                      <ArticleForm article={a} onDone={() => { setEditing(null); load(); loadManage(); }} />
                    </DialogContent>
                  </Dialog>
                  <Button size="icon" variant="ghost" onClick={() => toggleHide(a)} data-testid={`hide-article-${a.id}`}>{a.status === "hidden" ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(a)} className="text-brand-terracotta" data-testid={`remove-article-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              {manage.length === 0 && <p className="text-brand-muted text-sm text-center py-10">No articles yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      ) : Grid(articles, "article-card")}

      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent className="max-w-2xl border-brand-line max-h-[85vh] overflow-y-auto scrollbar-thin">
          {reading && (
            <div data-testid="article-reader">
              <DialogHeader><DialogTitle className="font-display text-3xl text-left">{reading.title}</DialogTitle></DialogHeader>
              <p className="text-sm text-brand-muted">{reading.author_name} · {formatDate(reading.created_at)}</p>
              {articleImg(reading) && <img src={articleImg(reading)} alt="" className="rounded-xl w-full h-56 object-cover mt-4 border border-brand-line" />}
              <div className="mt-4 text-brand-ink leading-relaxed whitespace-pre-line">{reading.body}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
